/**
 * Shared in-memory cache for Dashboard V2 goals.
 * Lets GoalsCard and GoalsWorkspace reuse the same payload so
 * "View All Goals" opens instantly when the card already loaded.
 */

const TTL_MS = 60_000;

let cache = {
    userId: null,
    groups: null,
    fetchedAt: 0,
};

export function emptyGoalGroups() {
    return { inProgress: [], completed: [], pending: [] };
}

/** Pending comes from API as area groups with nested `goals`. Flatten for V2 lists. */
function flattenPending(pending) {
    if (!Array.isArray(pending)) return [];
    const looksGrouped = pending.some(
        (item) => item && Array.isArray(item.goals)
    );
    if (!looksGrouped) {
        return pending.filter((g) => g && g.id != null);
    }
    return pending.flatMap((group) => {
        if (!group || !Array.isArray(group.goals)) return [];
        return group.goals.map((goal) => ({
            ...goal,
            areaId: goal.areaId ?? group.areaId,
            areaName: goal.areaName || group.areaName,
            color: goal.color || group.color,
        }));
    });
}

export function normalizeGoalGroups(data) {
    if (!data) return emptyGoalGroups();
    const inProgress = Array.isArray(data.inProgress)
        ? [...data.inProgress].sort(
              (a, b) => (a.isOneGoal ? 0 : 1) - (b.isOneGoal ? 0 : 1)
          )
        : [];
    return {
        inProgress,
        completed: Array.isArray(data.completed) ? data.completed : [],
        pending: flattenPending(data.pending),
    };
}

export function getCachedGoalGroups(userId) {
    if (!userId || cache.userId !== userId || !cache.groups) return null;
    const pending = cache.groups.pending;
    if (Array.isArray(pending) && pending.some((item) => Array.isArray(item?.goals))) {
        cache.groups = {
            ...cache.groups,
            pending: flattenPending(pending),
        };
    }
    return cache.groups;
}

export function getFreshCachedGoalGroups(userId) {
    if (!userId || cache.userId !== userId || !cache.groups) return null;
    if (Date.now() - cache.fetchedAt > TTL_MS) return null;
    return getCachedGoalGroups(userId);
}

export function setCachedGoalGroups(userId, groups) {
    if (!userId || !groups) return;
    cache = {
        userId,
        groups: {
            ...groups,
            pending: flattenPending(groups.pending),
        },
        fetchedAt: Date.now(),
    };
}

export function patchCachedInProgress(userId, inProgressList) {
    if (!userId) return;
    const current = getCachedGoalGroups(userId) || emptyGoalGroups();
    setCachedGoalGroups(userId, {
        ...current,
        inProgress: Array.isArray(inProgressList) ? inProgressList : [],
    });
}

export function invalidateGoalsCache(userId) {
    if (userId && cache.userId !== userId) return;
    cache = { userId: null, groups: null, fetchedAt: 0 };
}
